import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Parse from 'parse';

const CreateEditAdminDialog = ({ open, onClose, onSave, admin, mode }) => {
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    email: '',
    phone: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (mode === 'edit' && admin) {
      setFormData({
        name: admin.name || '',
        username: admin.username || '',
        email: admin.email || '',
        phone: admin.phone || '',
        password: ''
      });
    } else {
      setFormData({
        name: '',
        username: '',
        email: '',
        phone: '',
        password: ''
      });
    }
    setErrors({});
    setShowPassword(false);
  }, [mode, admin, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleNameChange = (e) => {
    const cleanedValue = e.target.value.replace(/[^A-Za-z ]/g, '');
    setFormData((prev) => ({ ...prev, name: cleanedValue }));
  };

  const handleUsernameChange = (e) => {
    const cleanedValue = e.target.value.replace(/[^A-Za-z ]/g, '');
    setFormData((prev) => ({ ...prev, username: cleanedValue }));
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 10) value = value.slice(0, 10);
    setFormData((prev) => ({ ...prev, phone: value }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (mode === 'create') {
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      if (mode === 'create') {
        const createData = {
          name: formData.name,
          username: formData.username,
          email: formData.email,
          password: formData.password
        };
        if (formData.phone) {
          createData.phone = formData.phone;
        }
        await Parse.Cloud.run('createAdmin', createData);
      } else {
        const updateData = {
          adminId: admin.id,
          name: formData.name,
          username: formData.username,
          email: formData.email
        };
        if (formData.phone) {
          updateData.phone = formData.phone;
        }
        await Parse.Cloud.run('updateAdmin', updateData);
      }

      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving admin:', error);
      setErrors({ submit: error.message || 'Failed to save admin' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#2B2B2B',
      color: '#fff',
      '& input': { padding: '10px 14px' }
    },
    '& .MuiFormHelperText-root': { color: '#F44336' }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { backgroundColor: '#242424', m: 0 } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          backgroundColor: '#242424',
          color: '#FFC107',
          pb: 2
        }}
      >
        <Typography sx={{ fontSize: '20px', fontWeight: 600 }}>
          {mode === 'create' ? 'Create New Admin' : 'Edit Admin'}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: '#242424', pt: 2, pb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {errors.submit && (
            <Typography sx={{ color: '#F44336', fontSize: '14px', textAlign: 'center' }}>
              {errors.submit}
            </Typography>
          )}

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px', mb: 0.5 }}>
                Name *
              </Typography>
              <TextField
                name="name"
                fullWidth
                placeholder="Enter name"
                value={formData.name}
                onChange={handleNameChange}
                error={!!errors.name}
                helperText={errors.name}
                disabled={loading}
                sx={textFieldSx}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px', mb: 0.5 }}>
                Username *
              </Typography>
              <TextField
                name="username"
                fullWidth
                placeholder="Enter username"
                value={formData.username}
                onChange={handleUsernameChange}
                error={!!errors.username}
                helperText={errors.username}
                disabled={loading}
                sx={textFieldSx}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px', mb: 0.5 }}>
                Email *
              </Typography>
              <TextField
                name="email"
                type="email"
                fullWidth
                placeholder="Enter email"
                value={formData.email}
                onChange={handleChange}
                error={!!errors.email}
                helperText={errors.email}
                disabled={loading}
                sx={textFieldSx}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px', mb: 0.5 }}>
                Phone
              </Typography>
              <TextField
                name="phone"
                fullWidth
                placeholder="Enter phone"
                value={formData.phone}
                onChange={handlePhoneChange}
                disabled={loading}
                sx={textFieldSx}
                inputProps={{ maxLength: 10 }}
              />
            </Box>
          </Box>

          {mode === 'create' && (
            <Box>
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px', mb: 0.5 }}>
                Password *
              </Typography>
              <TextField
                name="password"
                type={showPassword ? 'text' : 'password'}
                fullWidth
                placeholder="Enter password"
                value={formData.password}
                onChange={handleChange}
                error={!!errors.password}
                helperText={errors.password}
                disabled={loading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={textFieldSx}
              />
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={loading}
              sx={{
                flex: 1,
                borderColor: 'rgba(255, 255, 255, 0.25)',
                color: '#fff',
                textTransform: 'uppercase',
                py: 1.2,
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.45)',
                  backgroundColor: 'rgba(255, 255, 255, 0.04)'
                },
                '&.Mui-disabled': {
                  borderColor: 'rgba(255, 255, 255, 0.12)',
                  color: 'rgba(255, 255, 255, 0.35)'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
              sx={{
                flex: 1,
                backgroundColor: '#FFC107',
                color: '#000',
                textTransform: 'uppercase',
                py: 1.2,
                '&:hover': {
                  backgroundColor: '#FFD54F'
                },
                '&.Mui-disabled': {
                  backgroundColor: 'rgba(255, 255, 255, 0.12)',
                  color: 'rgba(255, 255, 255, 0.5)'
                }
              }}
            >
              {loading ? (
                <CircularProgress size={20} sx={{ color: '#000' }} />
              ) : mode === 'create' ? (
                'Create'
              ) : (
                'Update'
              )}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CreateEditAdminDialog;
